<template>
	<!--
		Shows a text which displays when a store is open.

		Different scenarios for open hours are are:
		- Open: store is open now.
		- Today: store opens later today.
		- Tomorrow: store opens tomorrow.
		- On day X: store does not open until day X.
		- Nothing: store does not have any open hours information.
	-->
	<div class="is-store-open">
		<span v-if="openMessage" :style="computedStyle">
			{{ openMessage }}<span v-if="openHours">: kl {{ openHours }}</span>
		</span>
		<span v-else :style="computedStyle">Stängt idag</span>
	</div>
</template>

<script>
import format from 'date-fns/format';
import svLocale from 'date-fns/locale/sv';

export default {
	props: {
		store: {
			type: Object,
			required: true,
		},
	},
	data () {
		return {
			// Open hours interval that is currently valid.
			activeHoursInterval: null,
			// Global date of today (makes debugging easier)
			todaysDate: new Date(),
		};
	},
	computed: {
		computedStyle () {
			let color = '#808080'; // Gray.
			if (! this.closedToday) {
				color = '#e37604'; // Orange
			}

			if (this.isOpenNow) {
				color = '#149848'; // Green.
			}

			return { color };
		},
		currentDay () {
			return this.todaysDate.getDay();
		},
		isOpenNow () {
			const now = this.todaysDate;
			return this.currentDay === this.openWhen.day &&
				now >= this.openWhen.opens && now < this.openWhen.closes;
		},
		closedToday () {
			const now = this.todaysDate;
			const abnormalClosedToday = this.store.abnormalOpenHours.some(abnormalOpenHours => {
				return this.isClosedOnDate(abnormalOpenHours) &&
					this.sameDate(now, new Date(abnormalOpenHours.date));
			});
			if (! abnormalClosedToday) {
				return this.openWhen.day !== this.currentDay;

			}
			return true;
		},
		/**
		 * Returns an object which tells when the store is open. It will either
		 * be this day, or the next day when the store is open (closest to this point in time).
		 *
		 * Format of returned object.
		 * {
		 *	day (Number),
		 *	opens (Date),
		 *	closes (Date),
		 * }
		 */
		openWhen () {
			const now = this.todaysDate;

			// Check if store is open today.
			const openToday = this.getOpenHoursOnDate(now);
			if (openToday && now < openToday.closes) {
				return openToday;
			}
			// If the store isn't open today, then find the earliest day when it is,
			// by looking at each of the coming six days.p
			for (let i = 1; i < 7; ++i) {
				const date = new Date(now);
				date.setDate(date.getDate() + i);
				const openOnDay = this.getOpenHoursOnDate(date);
				if (openOnDay) {
					return openOnDay;
				}
			}
			return null;
		},
		openHours () {
			if (! this.openWhen){
				return null;
			}
			const openMinutes = this.openWhen.opens.getMinutes();
			const closesMinutes = this.openWhen.closes.getMinutes();
			const openHour = this.openWhen.opens.getHours();
			const closesHour = this.openWhen.closes.getHours();
			return (openMinutes === 0 ? openHour : (openHour + ':' + openMinutes)) + '-'
				+ (closesMinutes === 0 ? closesHour : (closesHour + ':' + closesMinutes));
		},
		openMessage () {
			const now = this.todaysDate;
			if (! this.openWhen) {
				return 'Stängt';
			}

			if (this.currentDay === this.openWhen.day && now >= this.openWhen.opens
				&& now < this.openWhen.closes) {
				return 'Öppet nu';
			} else if (this.currentDay === this.openWhen.day && now < this.openWhen.opens) {
				return 'Idag';
			} else if (this.addDaysToWeekDay(this.currentDay, 1) === this.openWhen.day) {
				return 'Imorgon';
			} else if (this.currentDay !== this.openWhen.day) {
				return 'På ' + format(new Date(this.openWhen.opens), 'dddd', {
					locale: svLocale,
				});
			}
			return null;
		},
	},
	methods: {
		/**
		 * Gets the correct open hour choice for a store during a certain date.
		 */
		getOpenHoursOnDate (date) {
			const day = date.getDay();
			for (const abnormalOpenHours of this.store.abnormalOpenHours) {
				const abnormalDate = new Date(abnormalOpenHours.date);
				if (this.isClosedOnDate(abnormalOpenHours) && this.sameDate(abnormalDate, date)) {
					// The store is closed on this date.
					return null;
				}
				const { opens, closes } = this.getOpensClosesTimes(abnormalOpenHours, null, true);
				if (this.sameDate(date, opens)) {
					// The store has abnormal open hours this date, but is open.
					return {
						day,
						opens,
						closes,
					};
				}
			}
			for (const openHours of this.store.regularOpenHours) {
				const { opens, closes } = this.getOpensClosesTimes(openHours, day);
				if (this.dayInInterval(day, openHours)) {
					// The store is regularly open on this date.
					return {
						day,
						opens,
						closes,
					};
				}
			}
			return null;
		},
		isClosedOnDate (abnormalOpenHours) {
			return ! abnormalOpenHours.opening;
		},
		addDaysToWeekDay (day, daysToAdd) {
			return (day + daysToAdd) % 7;
		},
		/**
		 * Gets the open and closes times of a regular or abnormal open hours object.
		 *
		 * When getting times for a regular object, the date used is today.
		 * When getting times for an abnormal object, the abnormal open date is used.
		 */
		getOpensClosesTimes (openHours, day, abnormal = false) {
			let date = null;
			if (! abnormal) {
				// Set the date to the day (in this week), given as an parameter.
				date = new Date(this.todaysDate);
				const currentDay = date.getDay();
				date.setDate(date.getDate() + (day - currentDay));
			} else {
				// Set the date to the abnormal open date.
				date = new Date(openHours.date);
			}
			const closes = new Date(date);
			const opens = new Date(date);
			if (! openHours.closing || ! openHours.opening) {
				closes.setHours(0, 0, 0, 0);
				opens.setHours(0, 0, 0, 0);
			} else {
				const closesTime = this.getHourMinutes(openHours.closing);
				closes.setHours(closesTime.hour, closesTime.minutes, 0, 0);
				const opensTime = this.getHourMinutes(openHours.opening);
				opens.setHours(opensTime.hour, opensTime.minutes, 0, 0);
			}
			return {
				opens,
				closes,
			};
		},
		getHourMinutes(hrMinStr) {
			const parts = hrMinStr.split(':');
			return {
				hour: ~~parts[0],
				minutes: ~~parts[1],
			};
		},
		/**
		 * Checks if a day lies within a given regular open hours interval.
		 */
		dayInInterval (jsDay, openHours) {
			const iso8601Day = jsDay || 7;
			if (openHours.toWeekday) {
				return iso8601Day >= openHours.fromWeekday && (iso8601Day <= openHours.toWeekday);
			} else {
				return iso8601Day === openHours.fromWeekday;
			}
		},
		sameDate (date1, date2) {
			return date1.getFullYear() === date2.getFullYear() &&
				date1.getMonth() === date2.getMonth() &&
				date1.getDate() === date2.getDate();
		},
	},
};
</script>
